import { CircleCheckBig, FileText, CircleX, User as UserIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import Badge from '../../components/common/Badge';
import type { TraineeAssessmentModuleInstanceDataFragment } from './TraineeAssessment.api';
import { AssessmentRunStatus, ModuleCategory } from '@/apolloClient.types';
import Button from '@/components/common/Button';
import GenericModuleCard from '@/components/common/GenericModuleCard';
import { useUser } from '@/contexts/UserSession';
import buildFromLocation from '@/utils/buildFromLocation';
import { getIconBackground, getModuleIcon } from '@/utils/moduleIcons';
import useDateFormats from '@/utils/useDateFormats';

interface AssessmentCardProps {
    moduleInstance: TraineeAssessmentModuleInstanceDataFragment;
    isCompleted: boolean;
    completedAt: string | Date | null;
    expiredOn: Date;
    assessmentRunStatus: AssessmentRunStatus;
    assessmentRunId: string;
    courseInstanceId: string;
    targetUserId?: string;
    targetFullName?: string;
    resultId?: string;
    onViewResult: (resultId: string | undefined) => void;
}

const AssessmentCard = ({
    moduleInstance: assessment,
    isCompleted,
    completedAt,
    expiredOn,
    assessmentRunStatus,
    assessmentRunId,
    courseInstanceId,
    targetUserId,
    targetFullName,
    resultId,
    onViewResult,
}: AssessmentCardProps) => {
    const { t } = useTranslation('traineeAssessmentList');
    const { formatLongDate } = useDateFormats();
    const navigate = useNavigate();
    const location = useLocation();
    const currentUser = useUser(true);

    const moduleIcon = useMemo(() => {
        return getModuleIcon(assessment.moduleDefinition?.groupingKey || assessment.moduleDefinitionIdentifier);
    }, [assessment.moduleDefinition, assessment.moduleDefinitionIdentifier]);

    const isExpired = useMemo(() => {
        return assessmentRunStatus === AssessmentRunStatus.Ended;
    }, [assessmentRunStatus]);

    const isPending = useMemo(() => {
        return assessmentRunStatus === AssessmentRunStatus.NotStarted;
    }, [assessmentRunStatus]);

    const iconBackground = useMemo(
        () =>
            getIconBackground(
                ModuleCategory.Assessment,
                assessment.moduleDefinition?.__typename === 'AssessmentModuleDefinition'
                    ? assessment.moduleDefinition.subCategory || undefined
                    : undefined
            ),
        [assessment.moduleDefinition]
    );

    const headerAction = useMemo(() => {
        if (!isExpired) {
            return null;
        }

        return (
            <div className="flex items-center gap-2">
                <Badge text={t('status.expired')} color={'gray'} className="rounded-md" />
            </div>
        );
    }, [isExpired, t]);

    const handleStartAssessment = useCallback(() => {
        const search = new URLSearchParams({ mode: 'test', ...(targetUserId ? { targetUserId } : {}) });
        navigate(`/content/play/${assessment.id}/${assessmentRunId}/${courseInstanceId}?${search.toString()}`, {
            state: { from: buildFromLocation(location) },
        });
    }, [assessment.id, assessmentRunId, courseInstanceId, targetUserId, navigate, location]);

    const contentItems = useMemo(() => {
        const items = [];

        // Show target user if present and not self
        const isSelf = targetUserId ? targetUserId === currentUser.id : true;
        if (targetUserId && !isSelf && currentUser.__typename === 'TenantUser') {
            const label = targetFullName || t('targetUser', { defaultValue: 'Target user' });
            items.push({ icon: UserIcon, iconClassName: 'size-3', text: label });
        }

        if (isCompleted && completedAt) {
            items.push({
                icon: CircleCheckBig,
                iconColor: 'teal',
                iconClassName: 'size-3',
                text: `${t('completedOn')} ${formatLongDate(completedAt)}`,
            });
        }
        return items;
    }, [
        targetUserId,
        currentUser.id,
        currentUser.__typename,
        isCompleted,
        completedAt,
        targetFullName,
        t,
        formatLongDate,
    ]);

    const footer = useMemo(() => {
        if (isPending) {
            return null;
        } else if (!isExpired && !isCompleted) {
            return (
                <div>
                    <Button
                        type="button"
                        color="teal"
                        className="flex w-full items-center gap-2"
                        onClick={handleStartAssessment}
                    >
                        {t('actions.startAssessment')}
                    </Button>
                </div>
            );
        } else if (isCompleted) {
            return (
                <div>
                    <Button
                        type="button"
                        ignoreVariantStyle
                        className="flex w-full items-center gap-2 border border-teal-200 bg-teal-50 text-teal-600"
                        onClick={() => onViewResult(resultId)}
                    >
                        <FileText className="size-4" />
                        {t('actions.viewResults')}
                    </Button>
                </div>
            );
        }

        return (
            <div className="flex items-center gap-2 text-base font-medium text-neutral-500">
                <CircleX className="size-4" color="red" />
                {t('expiredOn', {
                    expiredOn: formatLongDate(expiredOn),
                })}
            </div>
        );
    }, [
        expiredOn,
        formatLongDate,
        handleStartAssessment,
        isCompleted,
        isExpired,
        isPending,
        onViewResult,
        resultId,
        t,
    ]);

    return (
        <GenericModuleCard
            headerAction={headerAction}
            isDisabled={isExpired && !isCompleted}
            icon={moduleIcon}
            iconBackground={iconBackground}
            title={assessment.name}
            description={assessment.description || t('noDescription')}
            contentItems={contentItems}
            footer={footer}
        />
    );
};

export default AssessmentCard;
